import { nextTick, type ComponentInternalInstance, ref, shallowRef } from 'vue';
import { Lrud, type Direction as LrudDirection, type NodeConfig, type Node, type NodeId } from 'lrud';
import { Directions } from './interfaces';
import { KeyCodes } from './key-codes';
import { LrudEmitter, type CallbackParameters } from './lrud-emitter';
import { appKeepalive } from '#build/nuxt.config.mjs';

type FocusHistory = {
    page: string;
    focus: string | undefined;
};

type RegisterOptions = {
    parent?: ComponentInternalInstance | string;
    awaitSiblings?: boolean;
};

type FocusRecoveryOptions = {
    preferredNode?: NodeId | Node;
    excludedNode?: NodeId | Node;
    force?: boolean;
};

type PendingFocusRequest = {
    nodeId: NodeId;
    routeToken: number;
    focusToken: number;
    timer: ReturnType<typeof setTimeout>;
};

type PendingDescendantFocusRequest = PendingFocusRequest & {
    handler: (node: Node) => void;
};

const LRUD_DEBUG_STORAGE_KEY = 'lrud:debug';
const LRUD_PENDING_FOCUS_TIMEOUT = 3000;
const LRUD_RECOVERY_DELAY = 80;

export default () => {
    // create an instance, register some nodes and assign a default focus
    const base = new Lrud();
    const nuxtApp = useNuxtApp();

    let routeTransitionToken = 0;
    let focusRequestToken = 0;
    const pendingFocusRequests = new Map<NodeId, PendingFocusRequest>();
    const pendingDescendantFocusRequests = new Map<NodeId, PendingDescendantFocusRequest>();
    const recoveryTimers = new Set<ReturnType<typeof setTimeout>>();
    const routeLastFocusState = new Map<string, NodeId>();
    const routeDefaultFocusState = new Map<string, NodeId[]>();

    const getRoutePath = () => useRoute().path;

    const isDebugEnabled = () => {
        if (!process.client || typeof window === 'undefined') {
            return false;
        }
        const debugValue = window.localStorage.getItem(LRUD_DEBUG_STORAGE_KEY);
        return debugValue === '1' || debugValue === 'true';
    };

    const getKeyCode = (event: KeyboardEvent) => {
        const keyCode = Reflect.get(event, 'keyCode');
        return typeof keyCode === 'number' ? keyCode : undefined;
    };

    const isLrudDirection = (direction?: CallbackParameters['direction']): direction is LrudDirection => {
        return Boolean(direction && direction !== Directions.BACK);
    };

    const trace = (event: string, details: Record<string, unknown> = {}) => {
        if (!isDebugEnabled()) {
            return;
        }
        const logger = globalThis.console;
        logger.debug('[lrud]', event, {
            route: getRoutePath(),
            token: routeTransitionToken,
            focus: base.currentFocusNode?.id,
            ...details
        });
    };

    const isFocusableNode = (node: Node) =>
        node.isFocusable !== undefined && node.isFocusable !== null ? node.isFocusable : Boolean(node.selectAction);

    // Id узла для логов. Узел может быть ещё не зарегистрирован (ref пустой), поэтому проверяем
    const resolveNodeId = (node?: NodeId | Node): NodeId | undefined => (typeof node === 'string' ? node : node?.id);

    const resolveNode = (node?: NodeId | Node): Node | undefined => {
        if (!node) {
            return undefined;
        }
        const resolvedNode = typeof node === 'string' ? base.getNode(node) : node;
        if (!resolvedNode) {
            return undefined;
        }
        return base.getNode(resolvedNode.id) === resolvedNode ? resolvedNode : undefined;
    };

    const isCurrentFocusValid = () => {
        const currentFocusNode = base.currentFocusNode;
        return Boolean(currentFocusNode && resolveNode(currentFocusNode) && isFocusableNode(currentFocusNode));
    };

    const isNodeExcluded = (node: Node, excludedNode?: Node) => {
        return Boolean(excludedNode && base.isSameOrParentForChild(excludedNode, node));
    };

    const isCurrentFocusInside = (node: NodeId | Node) => {
        const realNode = resolveNode(node);
        const currentFocusNode = base.currentFocusNode;
        return Boolean(realNode && currentFocusNode && base.isSameOrParentForChild(realNode, currentFocusNode));
    };

    const findFirstFocusableNode = (startNode?: Node, excludedNode?: Node): Node | undefined => {
        if (!startNode) {
            return undefined;
        }

        const stack = [startNode];
        while (stack.length) {
            const currentNode = stack.pop();
            if (!currentNode || isNodeExcluded(currentNode, excludedNode)) {
                continue;
            }
            if (isFocusableNode(currentNode)) {
                return currentNode;
            }
            const children = currentNode.children || [];
            for (let index = children.length - 1; index >= 0; index--) {
                stack.push(children[index]);
            }
        }

        return undefined;
    };

    const digDownSafely = (node: Node) => {
        const previousIndexAlignMode = base.isIndexAlignMode;
        if (!base.currentFocusNode) {
            base.isIndexAlignMode = false;
        }
        try {
            return base.digDown(node, Directions.UNSPECIFIED);
        } catch (error) {
            trace('dig-down-failed', { nodeId: node.id, error });
            return undefined;
        } finally {
            if (base.currentFocusNode) {
                base.isIndexAlignMode = previousIndexAlignMode;
            }
        }
    };

    const resolveFocusableCandidate = (node?: NodeId | Node, excludedNode?: Node): Node | undefined => {
        const realNode = resolveNode(node);
        if (!realNode || isNodeExcluded(realNode, excludedNode)) {
            return undefined;
        }

        const focusableChild = digDownSafely(realNode);
        if (focusableChild && !isNodeExcluded(focusableChild, excludedNode)) {
            return focusableChild;
        }

        return findFirstFocusableNode(realNode, excludedNode);
    };

    const assignResolvedFocus = (node: Node, reason: string) => {
        try {
            base.assignFocus(node);
            trace('assign-focus:success', { reason, nodeId: node.id });
            return true;
        } catch (error) {
            trace('assign-focus:failed', { reason, nodeId: node.id, error });
            return false;
        }
    };

    const getFocusHistoryForRoute = (routePath: string) => {
        for (let index = focusHistoryState.value.length - 1; index >= 0; index--) {
            const historyItem = focusHistoryState.value[index];
            if (historyItem.page === routePath && historyItem.focus) {
                return historyItem.focus;
            }
        }
        return undefined;
    };

    const restoreFocusIfMissing = (reason: string, options: FocusRecoveryOptions = {}) => {
        const excludedNode = resolveNode(options.excludedNode);
        const currentFocusNode = base.currentFocusNode;
        const currentFocusAllowed =
            !currentFocusNode || !excludedNode || !isNodeExcluded(currentFocusNode, excludedNode);

        if (!options.force && isCurrentFocusValid() && currentFocusAllowed) {
            return true;
        }

        const routePath = getRoutePath();
        const routeDefaultFocusIds = routeDefaultFocusState.get(routePath) || [];
        const candidates: Array<NodeId | Node | undefined> = [
            options.preferredNode,
            routeLastFocusState.get(routePath),
            getFocusHistoryForRoute(routePath),
            ...routeDefaultFocusIds,
            base.rootNode
        ];

        trace('focus-recovery:start', {
            reason,
            force: options.force === true,
            excludedNodeId: excludedNode?.id,
            candidates: candidates.map((candidate) => (typeof candidate === 'string' ? candidate : candidate?.id))
        });

        for (const candidate of candidates) {
            const focusableNode = resolveFocusableCandidate(candidate, excludedNode);
            if (focusableNode && assignResolvedFocus(focusableNode, `recovery:${reason}`)) {
                return true;
            }
        }

        trace('focus-recovery:failed', { reason });
        return false;
    };

    const clearRecoveryTimers = () => {
        recoveryTimers.forEach((timer) => clearTimeout(timer));
        recoveryTimers.clear();
    };

    const scheduleFocusRecovery = (reason: string, options: FocusRecoveryOptions = {}, delay = LRUD_RECOVERY_DELAY) => {
        const token = routeTransitionToken;
        const timer = setTimeout(() => {
            recoveryTimers.delete(timer);
            if (token !== routeTransitionToken) {
                trace('focus-recovery:skip-stale-token', { reason, token });
                return;
            }
            restoreFocusIfMissing(reason, options);
        }, delay);
        recoveryTimers.add(timer);
    };

    const clearPendingFocusRequest = (nodeId: NodeId) => {
        const pendingRequest = pendingFocusRequests.get(nodeId);
        if (!pendingRequest) {
            return;
        }
        clearTimeout(pendingRequest.timer);
        pendingFocusRequests.delete(nodeId);
    };

    const clearPendingDescendantFocusRequest = (nodeId: NodeId) => {
        const pendingRequest = pendingDescendantFocusRequests.get(nodeId);
        if (!pendingRequest) {
            return;
        }
        clearTimeout(pendingRequest.timer);
        base.emitter.off('lrud:register', pendingRequest.handler);
        pendingDescendantFocusRequests.delete(nodeId);
    };

    const cancelPendingFocusRequests = (reason: string) => {
        focusRequestToken += 1;
        if (pendingFocusRequests.size || pendingDescendantFocusRequests.size) {
            trace('pending-focus:cancel-all', {
                reason,
                nodeIds: [...pendingFocusRequests.keys(), ...pendingDescendantFocusRequests.keys()]
            });
        }
        pendingFocusRequests.forEach((pendingRequest) => clearTimeout(pendingRequest.timer));
        pendingFocusRequests.clear();
        pendingDescendantFocusRequests.forEach((pendingRequest) => {
            clearTimeout(pendingRequest.timer);
            base.emitter.off('lrud:register', pendingRequest.handler);
        });
        pendingDescendantFocusRequests.clear();
    };

    const queueAssignFocus = (nodeId: NodeId, reason: string) => {
        cancelPendingFocusRequests(`superseded:${reason}`);
        const routeToken = routeTransitionToken;
        const currentFocusRequestToken = focusRequestToken;
        const timer = setTimeout(() => {
            const pendingRequest = pendingFocusRequests.get(nodeId);
            if (
                !pendingRequest ||
                pendingRequest.routeToken !== routeToken ||
                pendingRequest.focusToken !== currentFocusRequestToken
            ) {
                return;
            }
            pendingFocusRequests.delete(nodeId);
            trace('pending-focus:expired', { reason, nodeId });
            scheduleFocusRecovery(`pending-focus-expired:${reason}`);
        }, LRUD_PENDING_FOCUS_TIMEOUT);
        pendingFocusRequests.set(nodeId, {
            nodeId,
            routeToken,
            focusToken: currentFocusRequestToken,
            timer
        });
        trace('pending-focus:queued', { reason, nodeId });
    };

    const fulfillPendingFocusRequest = (node: Node) => {
        const pendingRequest = pendingFocusRequests.get(node.id);
        if (!pendingRequest) {
            return;
        }

        if (pendingRequest.routeToken !== routeTransitionToken || pendingRequest.focusToken !== focusRequestToken) {
            clearPendingFocusRequest(node.id);
            trace('pending-focus:skip-stale-token', {
                nodeId: node.id,
                routeToken: pendingRequest.routeToken,
                focusToken: pendingRequest.focusToken
            });
            return;
        }

        clearPendingFocusRequest(node.id);
        trace('pending-focus:registered', { nodeId: node.id });
        nextTick(() => {
            if (pendingRequest.routeToken !== routeTransitionToken || pendingRequest.focusToken !== focusRequestToken) {
                trace('pending-focus:skip-stale-next-tick', {
                    nodeId: node.id,
                    routeToken: pendingRequest.routeToken,
                    focusToken: pendingRequest.focusToken
                });
                return;
            }
            assignFocus(node);
        });
    };

    const waitForFocusableDescendant = (node: Node, reason: string) => {
        cancelPendingFocusRequests(`superseded-descendant:${reason}`);
        const routeToken = routeTransitionToken;
        const currentFocusRequestToken = focusRequestToken;
        const handler = (registeredNode: Node) => {
            const realNode = resolveNode(node);
            if (routeToken !== routeTransitionToken || currentFocusRequestToken !== focusRequestToken || !realNode) {
                clearPendingDescendantFocusRequest(node.id);
                return;
            }
            if (!base.isSameOrParentForChild(realNode, registeredNode)) {
                return;
            }

            const focusableNode = resolveFocusableCandidate(realNode);
            if (focusableNode) {
                clearPendingDescendantFocusRequest(node.id);
                assignResolvedFocus(focusableNode, `descendant-registered:${reason}`);
            }
        };
        const timer = setTimeout(() => {
            const pendingRequest = pendingDescendantFocusRequests.get(node.id);
            if (
                !pendingRequest ||
                pendingRequest.routeToken !== routeToken ||
                pendingRequest.focusToken !== currentFocusRequestToken
            ) {
                return;
            }
            clearPendingDescendantFocusRequest(node.id);
            trace('pending-descendant-focus:expired', { reason, nodeId: node.id });
            scheduleFocusRecovery(`pending-descendant-focus-expired:${reason}`);
        }, LRUD_PENDING_FOCUS_TIMEOUT);

        pendingDescendantFocusRequests.set(node.id, {
            nodeId: node.id,
            routeToken,
            focusToken: currentFocusRequestToken,
            timer,
            handler
        });
        base.emitter.on('lrud:register', handler);
        trace('pending-descendant-focus:queued', { reason, nodeId: node.id });
    };

    const focusNodeOrWaitForDescendant = (node: Node, reason: string) => {
        const focusableNode = resolveFocusableCandidate(node);
        if (focusableNode) {
            cancelPendingFocusRequests(`resolved:${reason}`);
            clearPendingDescendantFocusRequest(node.id);
            assignResolvedFocus(focusableNode, reason);
            return;
        }
        waitForFocusableDescendant(node, reason);
    };

    const markDefaultFocus = (node?: NodeId | Node) => {
        const routePath = getRoutePath();
        const nodeId = resolveNodeId(node);
        if (!nodeId) {
            return;
        }
        const defaults = routeDefaultFocusState.get(routePath) || [];
        if (!defaults.includes(nodeId)) {
            routeDefaultFocusState.set(routePath, [...defaults, nodeId]);
        }
        trace('default-focus:mark', { nodeId });
    };

    // Хранение истории состояния фокуса при переходах
    const focusHistoryState = shallowRef<FocusHistory[]>([]);
    function addFocusHistory(focusObject: FocusHistory) {
        const currentHistory = focusHistoryState.value;
        currentHistory.push(focusObject);
        // Учитываем сколько хранить в памяти
        if (currentHistory.length > appKeepalive.max) {
            currentHistory.splice(0, 1);
        }
        focusHistoryState.value = currentHistory;
    }

    base.emitter.on('focus', (node: Node) => {
        routeLastFocusState.set(getRoutePath(), node.id);
        trace('focus', { nodeId: node.id });
    });

    // Список пользовательских колбэков
    const emitter = new LrudEmitter();
    // Стандартный прослушиватель для нажатия клавиши, если его не перекрыли нижестоящие
    emitter.on(({ keyboardEvent, direction }) => {
        if (direction === Directions.BACK) {
            return;
        }
        const keyboardDirection = keyboardEvent ? KeyCodes[getKeyCode(keyboardEvent) || 0] : undefined;
        const resolvedDirection = isLrudDirection(direction)
            ? direction
            : isLrudDirection(keyboardDirection)
            ? keyboardDirection
            : undefined;
        if (resolvedDirection || keyboardEvent) {
            const hasValidFocus = isCurrentFocusValid();
            const handledNode = resolvedDirection
                ? base.handleKeyEvent({ direction: resolvedDirection }, { forceFocus: !hasValidFocus })
                : undefined;
            if (!handledNode) {
                scheduleFocusRecovery('keydown-unhandled', {}, 0);
            }
        }
    });

    // Регистрация обработчиков событий
    if (process.client && typeof document !== 'undefined') {
        document.addEventListener('keydown', (event: KeyboardEvent) => {
            event.preventDefault();
            const keyCode = getKeyCode(event);
            const callbackProperties = <CallbackParameters>{
                keyboardEvent: event,
                direction: keyCode ? KeyCodes[keyCode] : undefined
            };
            emitter.emit(callbackProperties);
        });
    }

    // Регистрация приложения как главного lrud блока
    nuxtApp.hook('app:mounted', (vueApp) => {
        if (vueApp._container?.id) {
            base.registerNode(vueApp._container.id, {
                orientation: undefined
            });
            trace('root:registered', { nodeId: vueApp._container.id });
        }
    });

    const isLoaded = ref(false);
    // При каждой загрузке страницы объявляем загрузку
    nuxtApp.hook('page:loading:start', () => {
        routeTransitionToken += 1;
        cancelPendingFocusRequests('page:loading:start');
        clearRecoveryTimers();

        // Пишем в историю какой был фокус
        if (base.currentFocusNode?.id) {
            const route = useRoute();
            // Записываем в историю, если не находится в исключениях
            const routePageName = route.matched?.[0].components?.default?.name;
            if (appKeepalive.exclude && (!routePageName || !appKeepalive.exclude.includes(routePageName))) {
                addFocusHistory({
                    page: route.path,
                    focus: base.currentFocusNode.id
                });
            }
        }
        isLoaded.value = false;
        trace('page:loading:start');
    });

    // При переходах в keep alive
    base.emitter.on('lrud:register', (node: Node) => {
        fulfillPendingFocusRequest(node);
        scheduleFocusRecovery('node-registered', {}, 0);
        if (node.id) {
            const route = useRoute();
            // Если фокус был на этом элементе
            const isPrevFocus = focusHistoryState.value.find(
                (item) => item.page === route.path && item.focus === node.id
            );
            if (isPrevFocus) {
                // Ждем пока все прогрузится (фокус могут сбить defaultFocus от lrudnode)
                setTimeout(() => {
                    focusNodeOrWaitForDescendant(node, 'history-restore');
                }, 50);
            }
        }
    });

    // Подгружаем ноды, которые загрузились раньше хука прогрузки страницы
    nuxtApp.hook('page:loading:end', () => {
        isLoaded.value = true;
        nextTick(() => {
            base.emitter.emit('page:loaded');
            scheduleFocusRecovery('page:loading:end');
        });
    });

    nuxtApp.hook('app:error', () => {
        isLoaded.value = true;
        nextTick(() => {
            base.emitter.emit('page:loaded');
            scheduleFocusRecovery('app:error');
        });
    });

    // Обрабатываем nodeConfig
    const prepareNodeConfig = (nodeConfig: NodeConfig, options?: RegisterOptions): NodeConfig => {
        // Получаем родительский компонент, если не указан предок
        if (options?.parent && !nodeConfig?.parent) {
            let parent: ComponentInternalInstance | string | null | undefined = options.parent;
            if (typeof parent !== 'string') {
                while (parent && !nodeConfig?.parent) {
                    nodeConfig.parent = parent?.type.__name === 'LrudNode' ? parent.vnode.el?.id : undefined;
                    parent = parent.parent;
                }
            } else {
                nodeConfig.parent = parent;
            }
        }
        // Если предка нет, то это будет app
        if (!nodeConfig.parent) {
            nodeConfig.parent = nuxtApp.vueApp?._container?.id;
        }
        return nodeConfig;
    };

    // Регистрация lrud нод
    const register = (node: NodeId, nodeConfig: NodeConfig, options: RegisterOptions = {}): Promise<Node> => {
        return new Promise((resolve, reject) => {
            // Если страница еще не прогрузилась, делаем отложенный запуск
            if (!isLoaded.value) {
                const lazyRegister = () => {
                    base.emitter.off('page:loaded', lazyRegister);
                    resolve(register(node, nodeConfig, options));
                };
                base.emitter.on('page:loaded', lazyRegister);
                trace('register:deferred-page-loading', { nodeId: node });
                return;
            }

            nextTick(() => {
                const nodeConfigFormat = prepareNodeConfig(nodeConfig, options);
                // Если есть родитель и он зарегистрирован
                if (nodeConfigFormat.parent && !!base.getNode(nodeConfigFormat.parent)) {
                    // // Если вручную проставлен индекс (для onActivated восстановления)
                    if (options.awaitSiblings && nodeConfigFormat.index !== undefined && nodeConfigFormat.index > 0) {
                        const parent = base.getNode(nodeConfigFormat.parent);

                        // Если у родителя меньше дочерних элементов, чем нужный индекс
                        if (parent && (!parent.children || nodeConfigFormat.index > parent.children?.length)) {
                            const awaitSiblings = (siblingNodeAdd: Node) => {
                                if (
                                    nodeConfigFormat.parent === siblingNodeAdd.parent?.id &&
                                    siblingNodeAdd.index !== undefined &&
                                    siblingNodeAdd.index + 1 === nodeConfigFormat.index
                                ) {
                                    resolve(register(node, nodeConfig, options));
                                    base.emitter.off('lrud:register', awaitSiblings);
                                }
                            };
                            // Ожидаем регистрации соседа с предыдущим индексом
                            base.emitter.on('lrud:register', awaitSiblings);
                            return;
                        }
                    }

                    // Если этой ноды нет в древе, регистрируем
                    if (!base.getNode(node)) {
                        base.registerNode(node, nodeConfigFormat);
                    }
                    const nodeItem = base.getNode(node);
                    if (nodeItem) {
                        trace('register:success', { nodeId: nodeItem.id, parent: nodeItem.parent?.id });
                        base.emitter.emit('lrud:register', nodeItem);
                        return resolve(nodeItem);
                    } else {
                        return reject(new Error('Error: register lrud node'));
                    }
                } else if (nodeConfigFormat.parent && !base.getNode(nodeConfigFormat.parent)) {
                    const awaitParent = (awaitParentNode: Node) => {
                        if (awaitParentNode.id === nodeConfigFormat.parent) {
                            options.awaitSiblings = true;
                            resolve(register(node, nodeConfig, options));
                            base.emitter.off('lrud:register', awaitParent);
                        }
                    };
                    base.emitter.on('lrud:register', awaitParent);
                    trace('register:deferred-parent', { nodeId: node, parent: nodeConfigFormat.parent });
                    return;
                }

                // Такое может произойти, если до момента регистрации компонент пропал (реактивность)
                reject(new Error('Error: node not found'));
            });
        });
    };

    // Установка, возможен ли фокус на элементе
    const setNodeFocusable = (node: NodeId | Node, isFocusable: boolean): void => {
        if (!node) {
            return;
        }
        const realNode = resolveNode(node);
        const currentFocusWasInsideNode = realNode ? isCurrentFocusInside(realNode) : false;
        trace('set-node-focusable', {
            nodeId: resolveNodeId(node),
            isFocusable,
            currentFocusWasInsideNode
        });
        base.setNodeFocusable(node, isFocusable);

        if (realNode && !isFocusable && currentFocusWasInsideNode) {
            scheduleFocusRecovery(
                'set-node-unfocusable-current-subtree',
                { preferredNode: realNode.parent, excludedNode: realNode, force: true },
                0
            );
        }
    };

    // Фокус на элемент
    const assignFocus = (node?: NodeId | Node) => {
        // Узел ещё не зарегистрирован (например, ref заполняется только при регистрации) — тихо выходим
        if (!node) {
            trace('assign-focus:empty-node');
            return;
        }

        const realNode = resolveNode(node);
        if (!realNode) {
            if (typeof node === 'string') {
                queueAssignFocus(node, 'missing-node-id');
            } else {
                trace('assign-focus:stale-node', { nodeId: node.id });
                scheduleFocusRecovery('assign-focus-stale-node');
            }
            return;
        }

        clearPendingFocusRequest(realNode.id);
        focusNodeOrWaitForDescendant(realNode, 'assign-focus');
    };

    const handleNodeRemoved = (node: NodeId | Node, reason: string) => {
        const realNode = resolveNode(node);
        cancelPendingFocusRequests(`node-removed:${reason}`);
        if (!realNode) {
            scheduleFocusRecovery(reason, { force: true }, 0);
            return;
        }

        scheduleFocusRecovery(reason, { preferredNode: realNode.parent, excludedNode: realNode, force: true }, 0);
    };

    return {
        base,
        register,
        setNodeFocusable,
        assignFocus,
        emitter,
        markDefaultFocus,
        restoreFocusIfMissing,
        scheduleFocusRecovery,
        handleNodeRemoved,
        isCurrentFocusInside
    };
};
